import { Fragment, useEffect, useState } from 'react';

import styled from 'styled-components';

import { Rotate } from '@components/animators/Rotate';
import { Button } from '@components/button';
import { ComboMenu } from '@components/combo-menu';
import { EmptyState } from '@components/empty-state';
import { ExpandableCard } from '@components/expandable-card';
import { FocusBox } from '@components/focus-box';
import { HideContainer } from '@components/hide-container';
import { IconBranch } from '@components/icons/branch';
import { IconDropRight } from '@components/icons/dropright';
import { IconFormatText } from '@components/icons/format-text';
import { IconList } from '@components/icons/list';
import { IconMappingOff } from '@components/icons/mapping-off';
import { IconMappingOn } from '@components/icons/mapping-on';
import { IconReturn } from '@components/icons/return';
import { IconSearch } from '@components/icons/search';
import { IconTextShort } from '@components/icons/text-short';
import { InfoTooltip } from '@components/info-tooltip';
import { Menu } from '@components/menu';
import { SearchBox } from '@components/search-box';
import {
  CloseButton,
  ContentSeparator,
  hideExtendedSidebar,
  hideSidebar,
  Sidebar,
  SimpleIconHeader,
  useSidebar,
} from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text/text';
import { useTimeoutState } from '@hooks/use-timeout-state';
import { useScopedTranslate } from '@locale/translate';
import { useSelectionStore } from '@stores/create-selection-store';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { unasteriskLabel } from '@utils/strings';
import { MakePropertiesRequired, Transient } from '@utils/types';

import {
  extractName,
  FieldMappingData,
  FieldMappingFieldItem,
  FieldMappingGroup,
  FieldType,
  getPlaceholderHtml,
  getVariableLabel,
  MappableVariable,
} from './addons/field-mapping';
import {
  FIELD_MAPPING_ICON_COLOR,
  FIELD_MAPPING_ICON_SIZE,
  fieldTypeIconMap,
  fieldTypeLabelMap,
} from './addons/field-mapping-type-maps';
import { actionIcons, EditorRefProp } from './common';
import { FIELD_MAPPING_TEXT_EDITOR_CLASS_NAME } from './field-mapping-text-editor';
import { DateManualField } from './fields/date-field';
import { EmailManualField } from './fields/email-field';
import { FileManualField } from './fields/file-field';
import { NumberManualField } from './fields/number-field';
import { PhoneManualField } from './fields/phone-field';
import { SelectManualField } from './fields/select-field';

export type DateManualRestrictions = {
  disablePastDates?: boolean;
  disableFutureDates?: boolean;
  minDate?: Date;
  maxDate?: Date;
};

export type NumberManualRestrictions = {
  min?: number;
  max?: number;
  disableNegative?: boolean;
};

export type FieldMappingSidebarProps = MakePropertiesRequired<FieldMappingData, 'variableGroups'> &
  EditorRefProp & {
    onSelect?(): void;
    fieldType: FieldType;
    label?: string;
    fieldMappingId: string;
    onManualSelect?: (value: any) => void;
    manualData?: any;
    withCardBackground?: boolean;
    variable?: FieldMappingFieldItem;
    shouldHideMenu?: boolean;
    hideManualInput?: boolean;
    isStandardSidebar?: boolean;
    dateRestrictions?: DateManualRestrictions;
    numberRestrictions?: NumberManualRestrictions;
    onClose?(): void;
  };

const TRAVELLER_IDENTIFIER = 'traveller';
const JOURNEY_FIELDS_IDENTIFIER = 'journeyFields';
const JOURNEY_DATA_IDENTIFIER = 'journeyData';
const EXTERNAL_FORM_PREFIX = 'externalForm_';
const COMPANY_IDENTIFIER = 'company';
const ACTION_GROUPS_IDENTIFIER = 'actions-in-journey';
const EXTERNAL_FORMS_IDENTIFIER = 'external-forms';
const SIDEBAR_CONTAINER_CLASS_NAME = 'field-mapping-sidebar-container';

export const FIELD_MAPPING_SIDEBAR_ID = 'field-mapping-sidebar';

export const useStandardFieldMappingSidebar = () => {
  const sidebar = useSidebar(FieldMappingSidebar, {
    variant: 'REGULAR',
    zIndex: 9995,
    id: FIELD_MAPPING_SIDEBAR_ID,
  });
  return sidebar;
};

export const FieldMappingSidebar = ({
  variableGroups,
  editorRef,
  onSelect,
  fieldType,
  label = '',
  fieldMappingId,
  onManualSelect,
  manualData,
  variable,
  isStandardSidebar = false,
  shouldHideMenu = false,
  hideManualInput = false,
  dateRestrictions,
  numberRestrictions,
  onClose,
}: FieldMappingSidebarProps) => {
  const { t } = useScopedTranslate(locale);
  const [search, setSearch] = useState('');
  const [focusedId, setFocusedId] = useTimeoutState({ defaultValue: '', timeout: 1000 });
  const variableName = variable ? extractName(variable as MappableVariable) : '';

  const loweredSearch = search.toLowerCase();
  const filteredGroups = variableGroups
    .map((group) => {
      const filteredFields = group.fields.filter(
        (field) => field.type === 'separator' || field.label?.toLowerCase().includes(loweredSearch)
      );
      if (filteredFields.length === 0) {
        return null;
      }
      return {
        ...group,
        fields: filteredFields,
      };
    })
    .filter(Boolean) as FieldMappingGroup[];

  const travellerGroup = filteredGroups.find((group) => group.id === TRAVELLER_IDENTIFIER);
  const journeyFieldsGroup = filteredGroups.find((group) => group.id === JOURNEY_FIELDS_IDENTIFIER);
  const journeyDataGroup = filteredGroups.find((group) => group.id === JOURNEY_DATA_IDENTIFIER);
  const externalFormGroups = filteredGroups.filter((group) =>
    group.id.startsWith(EXTERNAL_FORM_PREFIX)
  );
  const companyGroup = filteredGroups.find((group) => group.id === COMPANY_IDENTIFIER);
  const actionGroups = filteredGroups.filter((group) => group.action != null);

  const otherGroups = filteredGroups.filter(
    (group) =>
      group.id !== COMPANY_IDENTIFIER &&
      group.id !== TRAVELLER_IDENTIFIER &&
      group.id !== JOURNEY_FIELDS_IDENTIFIER &&
      group.id !== JOURNEY_DATA_IDENTIFIER &&
      !group.id.startsWith(EXTERNAL_FORM_PREFIX) &&
      actionGroups.every((actionGroup) => actionGroup.id !== group.id)
  );

  const hasExternalForms = externalFormGroups.length > 0;

  const expandedStore = useSelectionStore(`field-mapping-expanded-${fieldMappingId}`, {
    [TRAVELLER_IDENTIFIER]: true,
    [JOURNEY_FIELDS_IDENTIFIER]: true,
    [JOURNEY_DATA_IDENTIFIER]: true,
    [COMPANY_IDENTIFIER]: true,
    [ACTION_GROUPS_IDENTIFIER]: true,
    [EXTERNAL_FORMS_IDENTIFIER]: true,
    ...Object.fromEntries(otherGroups.map((group) => [group.id, true])),
    ...Object.fromEntries(externalFormGroups.map((group) => [group.id, true])),
  });

  useEffect(() => {
    if (variableName) {
      const containingGroup = variableGroups.find((group) =>
        group.fields.some((field) => {
          const fieldName = extractName(field as MappableVariable);
          return fieldName === variableName;
        })
      );

      if (containingGroup) {
        if (containingGroup.action) {
          expandedStore.add(ACTION_GROUPS_IDENTIFIER);
        }
        expandedStore.add(containingGroup.id);
      }
      // We need to allow time for the sidebar to expand, else the focus effect
      // will be rendered behind the normal sidebar
      setTimeout(() => setFocusedId(variableName), 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variableName]);

  useEffect(() => {
    const handleFocus = (event: FocusEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;

      const isTextInput =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        (target.tagName === 'DIV' && target.className.includes('ProseMirror'));

      if (isTextInput) {
        const isInsideFieldMappingEditor = Boolean(
          target.closest(`.${FIELD_MAPPING_TEXT_EDITOR_CLASS_NAME}`)
        );
        const isInsideSidebar = Boolean(target.closest(`.${SIDEBAR_CONTAINER_CLASS_NAME}`));
        const isInsidePopup = Boolean(target.closest('.popover-panel'));

        if (!isInsideFieldMappingEditor && !isInsideSidebar && !isInsidePopup) {
          hideExtendedSidebar();
        }
      }
    };

    window.addEventListener('focusin', handleFocus);
    return () => window.removeEventListener('focusin', handleFocus);
  }, []);

  const handleSelect = (
    {
      options,
      optionsFromListId,
      excludedOptions,
      icon,
      iconRight,
      isActive,
      ...field
    }: FieldMappingFieldItem,
    insertType?: 'label' | 'value' | 'labelAndValue'
  ) => {
    onSelect?.();
    const variableLabel = getVariableLabel(field, variableGroups);
    const variableName = extractName(field as MappableVariable);
    const placeholder = getPlaceholderHtml({
      ...field,
      variable: variableName,
      label: variableLabel,
    });
    const content =
      insertType === 'labelAndValue'
        ? `${field.label}: ${placeholder}<p></p>`
        : insertType === 'label'
        ? field.label
        : placeholder;
    editorRef.current?.insertContent(content);
  };
  const hasActionGroups = actionGroups.length > 0;

  const isAllCollapsed =
    !expandedStore.isSelected(ACTION_GROUPS_IDENTIFIER) &&
    !expandedStore.isSelected(EXTERNAL_FORMS_IDENTIFIER) &&
    !expandedStore.isSelected(JOURNEY_FIELDS_IDENTIFIER) &&
    !expandedStore.isSelected(JOURNEY_DATA_IDENTIFIER) &&
    !filteredGroups.some((group) => !group.action && expandedStore.isSelected(group.id));

  const allExpandableIds = [
    JOURNEY_FIELDS_IDENTIFIER,
    JOURNEY_DATA_IDENTIFIER,
    TRAVELLER_IDENTIFIER,
    COMPANY_IDENTIFIER,
    ...(hasActionGroups ? [ACTION_GROUPS_IDENTIFIER] : []),
    ...(hasExternalForms ? [EXTERNAL_FORMS_IDENTIFIER] : []),
    ...filteredGroups.map((group) => group.id),
  ];

  const toggleAllExpanded = () => {
    if (isAllCollapsed) {
      expandedStore.add(allExpandableIds);
    } else {
      expandedStore.clear();
    }
  };

  const isEmptySearch = variableGroups.length > 0 && filteredGroups.length === 0 && search !== '';
  const isNoVariables = variableGroups.length === 0;

  const SidebarContainer = isStandardSidebar ? Sidebar.Content : Container;

  return (
    <SidebarContainer className={SIDEBAR_CONTAINER_CLASS_NAME}>
      <StickyHeader>
        <Stack space={4}>
          <Stack space={2}>
            <Stack space={2} orientation="horizontal" align="center" justify="space-between">
              <SimpleIconHeader icon={IconMappingOn} iconColor="blue100" title={t('fieldMap')} />
              <Stack space={2} orientation="horizontal" align="center">
                <CloseButton
                  onClose={onClose ?? (isStandardSidebar ? hideSidebar : hideExtendedSidebar)}
                />
              </Stack>
            </Stack>
            <Stack orientation="horizontal" space={4} justify="space-between" align="center">
              <SearchBox value={search} onChange={setSearch} placeholder={t('core.search')} />
              <Button
                variant="secondary"
                text={t(isAllCollapsed ? 'expandAll' : 'collapseAll')}
                onClick={toggleAllExpanded}
                iconRight={
                  <Rotate isRotated={!isAllCollapsed}>
                    <IconDropRight />
                  </Rotate>
                }
                style={{ minWidth: pxToRem(144) }}
              />
            </Stack>
          </Stack>
          <ContentSeparator />
        </Stack>
      </StickyHeader>
      <Stack space={4}>
        <Stack space={4}>
          <FieldLabelCard
            isShowingAll={variableGroups.length > 0 && !search}
            fieldType={fieldType}
            label={label}
            editorRef={editorRef}
            onManualSelect={onManualSelect}
            manualData={manualData}
            dateRestrictions={dateRestrictions}
            numberRestrictions={numberRestrictions}
            hideManualInput={hideManualInput}
            hasBackground
          />
          {isEmptySearch && (
            <EmptyState
              variant="small"
              skipMargin
              icon={<IconSearch />}
              header={t('noResultsFound')}
              content={t('noResultsMatchingSearch')}
            />
          )}
          {isNoVariables && <NoVariablesState />}
          {!isEmptySearch && !isNoVariables && (
            <Stack space={2}>
              {travellerGroup && (
                <VariableGroup
                  shouldHideMenu={shouldHideMenu}
                  group={travellerGroup}
                  description={t('travellerDescription')}
                  onSelect={handleSelect}
                  isCollapsed={!expandedStore.isSelected(TRAVELLER_IDENTIFIER)}
                  onCollapseToggle={() => expandedStore.toggle(TRAVELLER_IDENTIFIER)}
                  fieldType={fieldType}
                  focusId={focusedId}
                />
              )}
              {journeyFieldsGroup && (
                <VariableGroup
                  shouldHideMenu={shouldHideMenu}
                  group={journeyFieldsGroup}
                  description={t('journeyFieldsDescription')}
                  onSelect={handleSelect}
                  isCollapsed={!expandedStore.isSelected(JOURNEY_FIELDS_IDENTIFIER)}
                  onCollapseToggle={() => expandedStore.toggle(JOURNEY_FIELDS_IDENTIFIER)}
                  fieldType={fieldType}
                  focusId={focusedId}
                />
              )}
              {journeyDataGroup && (
                <VariableGroup
                  shouldHideMenu={shouldHideMenu}
                  group={journeyDataGroup}
                  description={t('journeyDataDescription')}
                  onSelect={handleSelect}
                  isCollapsed={!expandedStore.isSelected(JOURNEY_DATA_IDENTIFIER)}
                  onCollapseToggle={() => expandedStore.toggle(JOURNEY_DATA_IDENTIFIER)}
                  fieldType={fieldType}
                  focusId={focusedId}
                />
              )}
              {hasExternalForms && (
                <HideContainer
                  title={t('externalForms')}
                  counter={externalFormGroups.length}
                  isOpen={expandedStore.isSelected(EXTERNAL_FORMS_IDENTIFIER)}
                  key={EXTERNAL_FORMS_IDENTIFIER}
                  onClick={() => expandedStore.toggle(EXTERNAL_FORMS_IDENTIFIER)}
                >
                  <Stack space={2}>
                    {externalFormGroups.map((group) => (
                      <VariableGroup
                        key={group.id}
                        shouldHideMenu={shouldHideMenu}
                        isActionGroup
                        group={group}
                        onSelect={handleSelect}
                        isCollapsed={!expandedStore.isSelected(group.id)}
                        onCollapseToggle={() => expandedStore.toggle(group.id)}
                        fieldType={fieldType}
                        focusId={focusedId}
                      />
                    ))}
                  </Stack>
                </HideContainer>
              )}
              {companyGroup && (
                <VariableGroup
                  shouldHideMenu={shouldHideMenu}
                  group={companyGroup}
                  onSelect={handleSelect}
                  isCollapsed={!expandedStore.isSelected(COMPANY_IDENTIFIER)}
                  onCollapseToggle={() => expandedStore.toggle(COMPANY_IDENTIFIER)}
                  fieldType={fieldType}
                  focusId={focusedId}
                />
              )}
              {hasActionGroups && (
                <HideContainer
                  title={t('actionsInJourney')}
                  counter={actionGroups.length}
                  isOpen={expandedStore.isSelected(ACTION_GROUPS_IDENTIFIER)}
                  key={ACTION_GROUPS_IDENTIFIER}
                  onClick={() => expandedStore.toggle(ACTION_GROUPS_IDENTIFIER)}
                >
                  <Stack space={2}>
                    {actionGroups.map((group) => (
                      <VariableGroup
                        key={group.id}
                        shouldHideMenu={shouldHideMenu}
                        isActionGroup
                        group={group}
                        description={group.description}
                        onSelect={handleSelect}
                        isCollapsed={!expandedStore.isSelected(group.id)}
                        onCollapseToggle={() => expandedStore.toggle(group.id)}
                        fieldType={fieldType}
                        focusId={focusedId}
                      />
                    ))}
                  </Stack>
                </HideContainer>
              )}
              {otherGroups.map((group) => (
                <VariableGroup
                  key={group.id}
                  shouldHideMenu={shouldHideMenu}
                  group={group}
                  onSelect={handleSelect}
                  isCollapsed={!expandedStore.isSelected(group.id)}
                  onCollapseToggle={() => expandedStore.toggle(group.id)}
                  fieldType={fieldType}
                  focusId={focusedId}
                />
              ))}
            </Stack>
          )}
        </Stack>
      </Stack>
    </SidebarContainer>
  );
};

const NoVariablesState = () => {
  const { t } = useScopedTranslate(locale);
  return (
    <Stack space={4}>
      <EmptyState
        variant="small"
        skipMargin
        icon={<IconMappingOff />}
        header={t('noMappableFields')}
        content=""
      />
      <div>
        <Text text={t('possibleReasons')} font="regular14" color="textHelper" />
        <ul>
          <li style={{ color: theme.color.textHelper }}>
            <Text text={t('noActionsAbove')} font="regular14" color="textHelper" />
          </li>
          <li style={{ color: theme.color.textHelper }}>
            <Text text={t('noFieldsSameType')} font="regular14" color="textHelper" />
          </li>
        </ul>
      </div>
    </Stack>
  );
};

type VariableGroupProps = Pick<FieldMappingSidebarProps, 'fieldType' | 'shouldHideMenu'> & {
  group: FieldMappingGroup;
  description?: string;
  isCollapsed?: boolean;
  onSelect(field: FieldMappingFieldItem, insertType?: 'label' | 'value' | 'labelAndValue'): void;
  isActionGroup?: boolean;
  onCollapseToggle(id: string): void;
  focusId?: string;
};

const VariableGroup = ({
  shouldHideMenu,
  group,
  description,
  isCollapsed,
  onSelect,
  isActionGroup = false,
  onCollapseToggle,
  fieldType,
  focusId,
}: VariableGroupProps) => {
  const { t } = useScopedTranslate(locale);
  const Content = (
    <Stack space={4}>
      {!isActionGroup && description && (
        <Text text={description} font="regular14" color="textSecondary" />
      )}
      <Stack space={2}>
        {group.fields.map((field) => {
          const key = `${field.source}${field.field}`;
          if (field.type === 'separator') {
            return <FieldSeparator key={key} />;
          }
          const focusCompareField = extractName(field as MappableVariable);

          const DefaultIcon = fieldTypeIconMap[field.type ?? 'text'] ?? IconTextShort;
          const isSelect =
            (field.type === 'select' || field.type === 'multi_select') &&
            (field.label.includes('(label)') ||
              field.label.includes('(labels)') ||
              field.label.includes('(value)') ||
              field.label.includes('(values)'));

          if (!isSelect) {
            return (
              <FocusBox isFocused={focusId === focusCompareField} key={key} padding={4}>
                <Stack orientation="horizontal" space={2} align="center" justify="space-between">
                  <OverflowStack align="center">
                    {field.icon ?? (
                      <DefaultIcon
                        size={FIELD_MAPPING_ICON_SIZE}
                        color={FIELD_MAPPING_ICON_COLOR}
                      />
                    )}
                    <Text text={field.label} font="regular14" ellipsisWithTooltip />
                    {field.tooltip && <InfoTooltip tooltip={field.tooltip} />}
                  </OverflowStack>
                  <AddButton
                    field={field}
                    onSelect={onSelect}
                    fieldType={fieldType}
                    shouldHideMenu={shouldHideMenu}
                  />
                </Stack>
              </FocusBox>
            );
          }
          const isSelectLabel = field.label.includes('(label)') || field.label.includes('(labels)');

          const isSelectValue = field.label.includes('(value)') || field.label.includes('(values)');

          const LabelObject = (
            <OverflowStack align="center">
              <IconList size={FIELD_MAPPING_ICON_SIZE} color={FIELD_MAPPING_ICON_COLOR} />
              <Text
                text={field.label.replace('(label)', '').replace('(labels)', '')}
                font="regular14"
                ellipsisWithTooltip
              />
            </OverflowStack>
          );

          return (
            <Fragment key={key}>
              {isSelectLabel && LabelObject}
              <FocusBox isFocused={focusId === focusCompareField} padding={4}>
                <Stack orientation="horizontal" space={2} align="center" justify="space-between">
                  <OverflowStack>
                    <FieldIndentBorder $isValue={isSelectValue} />
                    <OverflowStack align="center">
                      <IconTextShort
                        size={FIELD_MAPPING_ICON_SIZE}
                        color={FIELD_MAPPING_ICON_COLOR}
                      />
                      <Text
                        text={isSelectValue ? t('optionValue') : t('optionLabel')}
                        font="regular14"
                        ellipsisWithTooltip
                      />
                    </OverflowStack>
                  </OverflowStack>
                  <AddButton
                    field={field}
                    onSelect={onSelect}
                    fieldType={fieldType}
                    shouldHideMenu={shouldHideMenu}
                  />
                </Stack>
              </FocusBox>
            </Fragment>
          );
        })}
      </Stack>
    </Stack>
  );

  if (isActionGroup) {
    const actionIcon = group.action ? actionIcons[group.action] : null;
    const groupIcon =
      group.icon ?? (actionIcon ? <actionIcon.Icon color={actionIcon.color} /> : null);
    return (
      <ExpandableCard
        title={group.label}
        icon={groupIcon}
        description={description}
        isOpen={!isCollapsed}
        onToggle={() => onCollapseToggle(group.id)}
        overflowType="visible"
      >
        {Content}
      </ExpandableCard>
    );
  }
  return (
    <HideContainer
      title={group.label}
      isOpen={!isCollapsed}
      key={group.id}
      onClick={() => onCollapseToggle(group.id)}
      overflowType="visible"
    >
      <ContentWrapper>{Content}</ContentWrapper>
    </HideContainer>
  );
};

type AddButtonProps = Pick<VariableGroupProps, 'onSelect' | 'fieldType' | 'shouldHideMenu'> & {
  field: FieldMappingFieldItem;
};

const AddButton = ({ field, onSelect, fieldType, shouldHideMenu }: AddButtonProps) => {
  const { t } = useScopedTranslate(locale);
  const hasMenu = !shouldHideMenu && ['text', 'paragraph'].includes(fieldType);

  return (
    <ComboMenu
      buttonProps={{
        variant: 'primary-blue',
        size: 'sm',
        text: t('core.add'),
        iconLeft: <IconReturn />,
        onClick: () => onSelect?.(field, 'value'),
        style: { flexShrink: 0 },
      }}
    >
      {hasMenu && (
        <>
          <Menu.Item
            label={t('addFieldLabelAndMapping')}
            extraIcon={
              <Stack orientation="horizontal" space={1}>
                <Button variant="secondary" size="xs">
                  <IconFormatText size={16} />
                </Button>
                <Button variant="primary-blue" size="xs">
                  <IconBranch size={16} />
                </Button>
              </Stack>
            }
            onSelect={() => onSelect?.(field, 'labelAndValue')}
          />
          <Menu.Item
            label={t('addFieldLabel')}
            extraIcon={
              <Button variant="secondary" size="xs">
                <IconFormatText size={16} />
              </Button>
            }
            onSelect={() => onSelect(field, 'label')}
          />
        </>
      )}
    </ComboMenu>
  );
};

type FieldLabelCardProps = MakePropertiesRequired<
  Pick<
    FieldMappingSidebarProps,
    | 'label'
    | 'fieldType'
    | 'editorRef'
    | 'onManualSelect'
    | 'manualData'
    | 'dateRestrictions'
    | 'numberRestrictions'
    | 'hideManualInput'
  > & {
    isShowingAll: boolean;
    hasBackground?: boolean;
  },
  'label' | 'fieldType'
>;

const FieldLabelCard = ({
  label,
  fieldType,
  isShowingAll,
  editorRef,
  onManualSelect,
  manualData,
  dateRestrictions,
  numberRestrictions,
  hideManualInput,
  hasBackground = false,
}: FieldLabelCardProps) => {
  const { t } = useScopedTranslate(locale);
  const Icon = fieldTypeIconMap[fieldType ?? 'text'] ?? IconTextShort;
  return (
    <FieldLabelCardContainer $hasBackground={hasBackground}>
      <Stack space={2}>
        <Stack orientation="horizontal" space={2} align="top" justify="space-between">
          <div>
            <Text text={unasteriskLabel(label)} font="semibold14" />
            <Text
              text={fieldTypeLabelMap[fieldType] ?? ''}
              font="regular12"
              color="textSecondary"
            />
          </div>
          <Icon color="elementPurple80" />
        </Stack>
        {isShowingAll && <Text text={t('displayingAll')} font="regular12" color="textSecondary" />}
        <ManuallyInputButton
          editorRef={editorRef}
          fieldType={fieldType}
          onManualSelect={onManualSelect}
          manualData={manualData}
          dateRestrictions={dateRestrictions}
          numberRestrictions={numberRestrictions}
          hideManualInput={hideManualInput}
        />
      </Stack>
    </FieldLabelCardContainer>
  );
};

export type ManuallyInputButtonProps = Pick<
  FieldMappingSidebarProps,
  | 'editorRef'
  | 'fieldType'
  | 'onManualSelect'
  | 'manualData'
  | 'dateRestrictions'
  | 'numberRestrictions'
  | 'hideManualInput'
>;

const ManuallyInputButton = ({
  editorRef,
  fieldType,
  onManualSelect,
  manualData,
  dateRestrictions,
  numberRestrictions,
  hideManualInput,
}: ManuallyInputButtonProps) => {
  if (hideManualInput) {
    return null;
  }
  if (fieldType === 'phone') {
    return <PhoneManualField editorRef={editorRef} />;
  }
  if (fieldType === 'email') {
    return <EmailManualField editorRef={editorRef} />;
  }
  if (fieldType === 'select') {
    if (!Array.isArray(manualData) || manualData.length === 0) {
      return null;
    }
    return <SelectManualField editorRef={editorRef} options={manualData} />;
  }
  if (fieldType === 'date') {
    return <DateManualField editorRef={editorRef} dateRestrictions={dateRestrictions} />;
  }
  if (fieldType === 'number') {
    return <NumberManualField editorRef={editorRef} numberRestrictions={numberRestrictions} />;
  }
  if (fieldType === 'decimal') {
    return (
      <NumberManualField editorRef={editorRef} isDecimal numberRestrictions={numberRestrictions} />
    );
  }
  if (fieldType === 'file' && onManualSelect) {
    return (
      <Stack orientation="horizontal">
        <FileManualField editorRef={editorRef} onManualSelect={onManualSelect} />
      </Stack>
    );
  }
  return null;
};

const FieldLabelCardContainer = styled.div<Transient<FieldLabelCardProps, 'hasBackground'>>(
  ({ $hasBackground }) => ({
    padding: pxToRem(16),
    borderRadius: pxToRem(8),
    border: `${pxToRem(1)} solid ${theme.color.border10}`,
    backgroundColor: $hasBackground ? theme.color.backgroundPrimary : 'transparent',
  })
);

const Container = styled.div({
  minHeight: '100%',
  // On the content, not the scroll container, so it shows at the end of scroll.
  paddingBottom: pxToRem(12),
  '.search-box-container': {
    flexGrow: 1,
  },
});

const StickyHeader = styled.div({
  position: 'sticky',
  top: 0,
  zIndex: 1,
  backgroundColor: theme.color.backgroundLightDark,
  paddingBottom: pxToRem(8),
});

const BORDER_WIDTH = 1;
export const FieldIndentBorder = styled.div<{ $isValue?: boolean }>(({ $isValue }) => ({
  width: pxToRem(BORDER_WIDTH),
  backgroundColor: theme.color.uiTrans20,
  marginLeft: pxToRem(12),
  marginRight: pxToRem(12 - BORDER_WIDTH),
  ...($isValue ? { marginTop: pxToRem(-20) } : {}),
}));

const ContentWrapper = styled.div({
  marginTop: pxToRem(-8),
});

const FieldSeparator = styled.div({
  height: pxToRem(1),
  backgroundColor: theme.color.border10,
  marginTop: pxToRem(8),
  marginBottom: pxToRem(8),
});

const OverflowStack = styled(Stack).attrs({
  orientation: 'horizontal',
  space: 2,
})({
  minWidth: 0,
});

export const locale = {
  en: {
    fieldMap: 'Field map',
    collapseAll: 'Collapse all',
    expandAll: 'Expand all',
    traveller: 'Traveller',
    travellerDescription: 'Global profile fields',
    company: 'Company',
    displayingAll: 'Displaying all available mappable fields',
    actionsInJourney: 'Actions in journey',
    addPhoneNumber: 'Add telephone number',
    addEmail: 'Add email address',
    emailLabel: 'Email',
    dateLabel: 'Date picker',
    manuallySelectDate: 'Manually select date',
    uploadFile: 'Upload file',
    manuallySelectValue: 'Manually select value',
    noResultsFound: 'No results found',
    noResultsMatchingSearch: 'No results matching your search',
    noMappableFields: 'No mappable fields',
    possibleReasons: 'Possible reasons',
    noActionsAbove: 'There are no actions above selected action',
    noFieldsSameType: 'There are no fields of the same type as the selected field',
    optionValue: 'Option value',
    optionLabel: 'Option label',
    numberLabel: 'Number',
    addNumber: 'Add number',
    addFieldLabelAndMapping: 'Add field label & mapping',
    addFieldLabel: 'Add field label',
    journeyFieldsDescription: 'Fields the traveller has in this journey',
    journeyDataDescription: 'Journey-specific traveller data',
    externalForms: 'External forms',
    externalFormsDescription: 'Fields in active use on external forms',
  },
};
